import React, { useEffect, useMemo, useState } from 'react'
import { StyleSheet, css } from 'aphrodite'
import { FormControl, FormHelperText, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Tooltip from '@material-ui/core/Tooltip'
import { ApiHelper } from '../helpers/ApiHelper'
import { Helper } from '../helpers/Helper'
import { AppButton } from './AppButton'
import { AppTheme } from '../../styles/AppTheme'
import { AppHelper } from '../../helpers/AppHelper'
import { RichtextEditor } from './RichtextComponent/RichtextEditor'
import { WhatsappTemplatePreview } from './WhatsappTemplatePreview'
import {
  buildAddModalWaTemplateFromForm,
  emptyWaBodyRichText,
  hooksBridgeErrorMessage,
  richTextToPlainStringForVariables,
  validateAddModalWaTemplate,
  WA_ADD_MODAL_HEADER_MEDIA_PLACEHOLDER_URLS,
  waRichTextToWhatsAppBodyText,
  waTemplateToActualWaTemplate,
} from '../../screens/admin/cdp/WhatsappTemplateDetail'
import { buildWaNumberSelectOptions, withVendorProjectIdQueryParam } from '../../screens/admin/cdp/WhatsappUtils'

const NONE = 'NONE'
const WA_MEDIA_CDN_BASE = 'https://d1bpnn2a5id540.cloudfront.net/'
const MAX_BUTTON_ROWS = 3
const FIELD_TIPS = {
  wabaNumber: 'WhatsApp Business Account phone number',
  title: 'Lowercase letters, numbers, underscores (snake_case)',
  headerText: 'Use {{1}}, {{2}}, ... for variables',
  bodyText: 'Bold, italic, and bullets. List items are sent as lines starting with "- " (WhatsApp). Use Variables to insert {{1}}, {{2}}, ...',
  footerText: 'Plain text only - variables like {{1}} are not supported in the footer.',
  buttonText: 'Plain text only, max 20 characters (no variables supported).',
  buttonUrl: 'A single {{1}} may appear at the end of the URL.',
  buttonPhone: 'Phone number for the call button',
  buttonFlow: 'Select the WhatsApp flow to launch from this button.',
}

const defaultForm = {
  wabaNumber: '',
  title: '',
  iType: 'UTILITY',
  headerType: NONE,
  headerText: '',
  bodyRichText: emptyWaBodyRichText(),
  footerText: '',
  headerTextVariableExample: '',
  bodyVariableExamplesByToken: {},
  buttonRows: [],
}

function newButtonRow() {
  return {
    id: `btn_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone: '',
    urlVariable: '',
    flowId: '',
  }
}

function urlHasVariablePlaceholder(url) {
  return /\{\{\d+\}\}/.test(String(url || ''))
}

function buttonRowsToLegacyFormFields(rows) {
  const list = (rows || []).slice(0, MAX_BUTTON_ROWS)
  const out = {}
  for (let slot = 1; slot <= MAX_BUTTON_ROWS; slot++) {
    const r = list[slot - 1]
    if (!r || !r.type) {
      out[`button${slot}Type`] = NONE
      out[`button${slot}Text`] = ''
      out[`button${slot}Url`] = ''
      out[`button${slot}Phone`] = ''
      out[`button${slot}UrlVariable`] = ''
      out[`button${slot}FlowId`] = ''
      continue
    }
    out[`button${slot}Type`] = r.type
    out[`button${slot}Text`] = String(r.text || '')
    if (r.type === 'URL') {
      out[`button${slot}Url`] = String(r.url || '')
      out[`button${slot}Phone`] = ''
      out[`button${slot}UrlVariable`] = urlHasVariablePlaceholder(r.url) ? String(r.urlVariable || '') : ''
      out[`button${slot}FlowId`] = ''
    } else if (r.type === 'PHONE') {
      out[`button${slot}Url`] = ''
      out[`button${slot}Phone`] = String(r.phone || '')
      out[`button${slot}UrlVariable`] = ''
      out[`button${slot}FlowId`] = ''
    } else if (r.type === 'FLOW') {
      out[`button${slot}Url`] = ''
      out[`button${slot}Phone`] = ''
      out[`button${slot}UrlVariable`] = ''
      out[`button${slot}FlowId`] = String(r.flowId || '')
    } else {
      out[`button${slot}Url`] = ''
      out[`button${slot}Phone`] = ''
      out[`button${slot}UrlVariable`] = ''
      out[`button${slot}FlowId`] = ''
    }
  }
  return out
}

function normalizeFlowsResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.flows)) return data.flows
  if (Array.isArray(data.data)) return data.data
  return []
}

export function WhatsappTemplateAddForm(props) {
  const {
    ms,
    orgId,
    waFromNumbers = [],
    waNumberDetails,
    crmNumbers = [],
    selectedWabaNumber = '',
    defaultTemplateName = '',
    onSubmitted,
    initialForm = null,
  } = props
  const [form, setForm] = useState({
    ...defaultForm,
    ...(initialForm || {}),
    wabaNumber: selectedWabaNumber || (waFromNumbers.length === 1 ? waFromNumbers[0] : ''),
    title: (initialForm && initialForm.title) || defaultTemplateName || defaultForm.title,
  })
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [inProcess, setInProcess] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [flowOptions, setFlowOptions] = useState([])

  const wabaOptions = useMemo(
    () => buildWaNumberSelectOptions(waFromNumbers, waNumberDetails),
    [waFromNumbers, waNumberDetails]
  )
  useEffect(() => {
    const lockedWaba = selectedWabaNumber || ''
    setForm(prev => {
      if (prev.wabaNumber === lockedWaba) return prev
      return { ...prev, wabaNumber: lockedWaba }
    })
  }, [selectedWabaNumber])

  useEffect(() => {
    const fromNumber = String(form.wabaNumber || '').trim()
    if (!fromNumber) {
      setFlowOptions([])
      return
    }
    let cancelled = false
    const fetchFlows = async () => {
      try {
        const data = await ApiHelper.callAwait({
          ms,
          method: 'GET',
          paths: ['hooks'],
          queryParams: withVendorProjectIdQueryParam({
            orgId,
            fromNumber,
            bridgePath: 'whatsappflows',
          }, fromNumber, crmNumbers),
        })
        if (cancelled) return
        const rows = normalizeFlowsResponse(data)
        const options = rows.map((row, idx) => {
          const keyRaw = row && row.id != null ? row.id : row && row.flowId != null ? row.flowId : ''
          const key = String(keyRaw || '').trim()
          if (!key) return null
          const name = String((row && (row.name || row.title)) || '').trim()
          return {
            key,
            label: name ? `${name} (${key})` : key,
          }
        }).filter(Boolean)
        setFlowOptions(options)
      } catch (e) {
        if (!cancelled) setFlowOptions([])
      }
    }
    fetchFlows()
    return () => { cancelled = true }
  }, [ms, orgId, form.wabaNumber, crmNumbers])
  const headerTextVariableMatch = String(form.headerText || '').match(/\{\{\d+\}\}/)
  const headerTextVariableToken = headerTextVariableMatch ? headerTextVariableMatch[0] : ''
  const hasHeaderTextVariable = Boolean(headerTextVariableToken)
  const bodyPlainForVars = richTextToPlainStringForVariables(form.bodyRichText || emptyWaBodyRichText())
  const bodyVariableTokensInOrder = useMemo(() => {
    const matches = String(bodyPlainForVars || '').match(/\{\{\d+\}\}/g) || []
    const seen = new Set()
    const out = []
    for (let i = 0; i < matches.length; i++) {
      const token = matches[i]
      if (!seen.has(token)) {
        seen.add(token)
        out.push(token)
      }
    }
    return out
  }, [bodyPlainForVars])

  const patchField = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value }
      if (key === 'headerType' && value !== 'TEXT') {
        next.headerText = ''
        next.headerTextVariableExample = ''
      }
      if (key === 'headerText' && !/\{\{\d+\}\}/.test(String(value || ''))) {
        next.headerTextVariableExample = ''
      }
      return next
    })
  }

  const showMessage = (text, error = false) => {
    setMessage(text || '')
    setIsError(Boolean(error))
  }

  const clearFieldError = (fieldKey) => {
    if (!fieldKey) return
    setFieldErrors(prev => {
      if (!prev[fieldKey]) return prev
      const next = { ...prev }
      delete next[fieldKey]
      return next
    })
  }

  const validateBeforeSubmit = () => {
    const errors = {}
    if (!String(form.wabaNumber || '').trim()) errors.wabaNumber = 'Select WABA number'
    if (!String(form.title || '').trim()) errors.title = 'Provide template name'
    if (!String(form.iType || '').trim()) errors.iType = 'Select category'
    if (!String(form.headerType || '').trim()) errors.headerType = 'Select header type'
    if (!String(bodyPlainForVars || '').trim()) errors.bodyRichText = 'Provide body text'

    if (form.headerType === 'TEXT') {
      if (!String(form.headerText || '').trim()) errors.headerText = 'Provide header text'
      if (hasHeaderTextVariable && !String(form.headerTextVariableExample || '').trim()) {
        errors.headerTextVariableExample = `Provide example for header text variable ${headerTextVariableToken}`
      }
    }

    bodyVariableTokensInOrder.forEach(token => {
      const key = `body-var-${token}`
      const value = (form.bodyVariableExamplesByToken && form.bodyVariableExamplesByToken[token]) || ''
      if (!String(value).trim()) errors[key] = `Provide example for body text variable ${token}`
    })

    ;(form.buttonRows || []).forEach(row => {
      if (!row || !row.id) return
      if (!String(row.type || '').trim()) errors[`${row.id}-type`] = 'Select button type'
      if (!String(row.text || '').trim()) errors[`${row.id}-text`] = 'Provide button text'
      if (row.type === 'PHONE' && !String(row.phone || '').trim()) {
        errors[`${row.id}-phone`] = 'Provide phone number'
      }
      if (row.type === 'URL') {
        if (!String(row.url || '').trim()) errors[`${row.id}-url`] = 'Provide URL'
        if (urlHasVariablePlaceholder(row.url) && !String(row.urlVariable || '').trim()) {
          const tokenMatch = String(row.url || '').match(/\{\{\d+\}\}/)
          const token = tokenMatch ? tokenMatch[0] : '{{1}}'
          errors[`${row.id}-urlVar`] = `Provide example for URL variable ${token}`
        }
      }
      if (row.type === 'FLOW' && !String(row.flowId || '').trim()) {
        errors[`${row.id}-flowId`] = 'Select flow'
      }
    })
    return errors
  }

  const submitTemplate = async () => {
    if (inProcess) return
    const inputErrors = validateBeforeSubmit()
    if (Object.keys(inputErrors).length > 0) {
      setFieldErrors(inputErrors)
      showMessage('')
      return
    }
    setFieldErrors({})
    setInProcess(true)
    showMessage('')
    try {
      const sortedBodyVarIndices = [...new Set(
        (String(bodyPlainForVars || '').match(/\{\{(\d+)\}\}/g) || [])
          .map(token => parseInt(String(token).replace(/[{}]/g, ''), 10))
          .filter(n => !Number.isNaN(n))
      )].sort((a, b) => a - b)
      const bodyTextVariableExamples = sortedBodyVarIndices
        .map(idx => {
          const token = `{{${idx}}}`
          const v = (form.bodyVariableExamplesByToken && form.bodyVariableExamplesByToken[token]) || ''
          return String(v).trim()
        })
        .join('|')
      const { buttonRows, ...formRest } = form
      const formData = {
        ...formRest,
        ...buttonRowsToLegacyFormFields(buttonRows),
        bodyRichText: form.bodyRichText || emptyWaBodyRichText(),
        bodyTextVariableExamples,
      }
      const wa = buildAddModalWaTemplateFromForm(formData)
      const err = validateAddModalWaTemplate(wa)
      if (err) {
        showMessage(err, true)
        setInProcess(false)
        return
      }
      const actualWaTemplate = waTemplateToActualWaTemplate(wa)
      const data = await ApiHelper.callAwait({
        ms,
        method: 'POST',
        paths: ['hooks'],
        queryParams: withVendorProjectIdQueryParam({
          orgId,
          bridgePath: 'whatsapptemplates',
        }, wa.wabaNumber, crmNumbers),
        jsonBody: {
          fromNumber: wa.wabaNumber,
          template: actualWaTemplate,
        },
      })
      const bridgeErr = hooksBridgeErrorMessage(data)
      if (bridgeErr) {
        showMessage(bridgeErr, true)
        setInProcess(false)
        return
      }
      if (data && data.status === 'REJECTED') {
        const rejectReason = data.rejected_reason || 'Template was rejected'
        showMessage(rejectReason, true)
        setInProcess(false)
        return
      }
      showMessage('The template was submitted successfully.', false)
      if (initialForm && initialForm.wabaNumber && form.wabaNumber !== initialForm.wabaNumber) {
        if (onSubmitted) onSubmitted({wabaNumber: form.wabaNumber, isDifferentWaba: true, template:actualWaTemplate ,isCopy: true })
      }  else if (initialForm && initialForm.wabaNumber) {if (onSubmitted) onSubmitted({ isDifferentWaba: false, isCopy: true })
      } else {
        if (onSubmitted) onSubmitted({ isDifferentWaba: false, isCopy: false })
      }
    } catch (err) {
      showMessage(Helper.getErrorMsg(err), true)
    } finally {
      setInProcess(false)
    }
  }

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault()
    await submitTemplate()
  }

  const handleRichtextInputChange = (value, key) => {
    if (key !== 'bodyRichText') {
      clearFieldError(key)
      patchField(key, value || emptyWaBodyRichText())
      return
    }
    clearFieldError('bodyRichText')
    patchField(key, value || emptyWaBodyRichText())
  }

  const patchBodyVariableExample = (token, value) => {
    clearFieldError(`body-var-${token}`)
    setForm(prev => ({
      ...prev,
      bodyVariableExamplesByToken: {
        ...(prev.bodyVariableExamplesByToken || {}),
        [token]: value,
      },
    }))
  }

  const addButtonRow = () => {
    setForm(prev => {
      if (prev.buttonRows.length >= MAX_BUTTON_ROWS) return prev
      return { ...prev, buttonRows: [...prev.buttonRows, newButtonRow()] }
    })
  }

  const removeButtonRow = (id) => {
    setForm(prev => ({ ...prev, buttonRows: prev.buttonRows.filter(r => r.id !== id) }))
  }

  const updateButtonRow = (id, patch) => {
    setForm(prev => ({
      ...prev,
      buttonRows: prev.buttonRows.map(r => {
        if (r.id !== id) return r
        let next = { ...r, ...patch }
        if (patch.type != null && patch.type !== r.type) {
          if (patch.type === 'QUICK_REPLY') {
            next = { ...next, url: '', phone: '', urlVariable: '', flowId: '' }
          } else if (patch.type === 'PHONE') {
            next = { ...next, url: '', urlVariable: '', flowId: '' }
          } else if (patch.type === 'URL') {
            next = { ...next, phone: '', flowId: '' }
          } else if (patch.type === 'FLOW') {
            next = { ...next, phone: '', url: '', urlVariable: '' }
          }
        }
        const urlVal = patch.url !== undefined ? patch.url : next.url
        if (!urlHasVariablePlaceholder(urlVal)) {
          next.urlVariable = ''
        }
        return next
      }),
    }))
  }

  const buttonTypeOptions = [
    { key: 'QUICK_REPLY', label: 'QUICK_REPLY' },
    { key: 'PHONE', label: 'PHONE' },
    { key: 'URL', label: 'URL' },
    { key: 'FLOW', label: 'FLOW' },
  ]

  const previewComponents = useMemo(() => {
    const out = []
    if (form.headerType === 'TEXT' && String(form.headerText || '').trim()) {
      const headerComp = {
        type: 'HEADER',
        format: 'TEXT',
        text: form.headerText,
      }
      if (hasHeaderTextVariable) {
        const headerIndexMatch = String(headerTextVariableToken || '').match(/\d+/)
        const headerVarIndex = headerIndexMatch ? parseInt(headerIndexMatch[0], 10) : 1
        const headerExamples = []
        headerExamples[Math.max(0, headerVarIndex - 1)] = String(form.headerTextVariableExample || '').trim()
        headerComp.example = {
          ['header_text']: headerExamples,
        }
      }
      out.push(headerComp)
    } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType)) {
      const mediaPath = String(WA_ADD_MODAL_HEADER_MEDIA_PLACEHOLDER_URLS[form.headerType] || '').trim()
      const mediaUrl = mediaPath ? `${WA_MEDIA_CDN_BASE}${mediaPath.replace(/^\//, '')}` : ''
      const headerComp = {
        type: 'HEADER',
        format: form.headerType,
      }
      if (mediaUrl) {
        headerComp.example = {
          ['header_handle']: [mediaUrl],
        }
      }
      out.push(headerComp)
    }
    const bodyComp = {
      type: 'BODY',
      text: waRichTextToWhatsAppBodyText(form.bodyRichText || emptyWaBodyRichText()),
    }
    if (bodyVariableTokensInOrder.length > 0) {
      const bodyExamplesByIndex = []
      bodyVariableTokensInOrder.forEach(token => {
        const idxMatch = String(token).match(/\d+/)
        const idx = idxMatch ? parseInt(idxMatch[0], 10) : 0
        if (idx > 0) {
          bodyExamplesByIndex[idx - 1] = String((form.bodyVariableExamplesByToken || {})[token] || '').trim()
        }
      })
      bodyComp.example = {
        ['body_text']: [bodyExamplesByIndex],
      }
    }
    out.push(bodyComp)
    if (String(form.footerText || '').trim()) {
      out.push({
        type: 'FOOTER',
        text: String(form.footerText || ''),
      })
    }
    const buttons = (form.buttonRows || [])
      .slice(0, MAX_BUTTON_ROWS)
      .map(row => {
        if (!row || !row.type) return null
        if (row.type === 'QUICK_REPLY') {
          return {
            type: 'QUICK_REPLY',
            text: String(row.text || ''),
          }
        }
        if (row.type === 'PHONE') {
          return {
            type: 'PHONE_NUMBER',
            text: String(row.text || ''),
            ['phone_number']: String(row.phone || ''),
          }
        }
        if (row.type === 'URL') {
          const button = {
            type: 'URL',
            text: String(row.text || ''),
            url: String(row.url || ''),
          }
          if (urlHasVariablePlaceholder(row.url)) {
            const idxMatch = String(row.url || '').match(/\{\{(\d+)\}\}/)
            const idx = idxMatch ? parseInt(idxMatch[1], 10) : 1
            const urlExamples = []
            if (idx > 0) {
              urlExamples[idx - 1] = String(row.urlVariable || '').trim()
            }
            button.example = urlExamples
          }
          return button
        }
        if (row.type === 'FLOW') {
          const button = {
            type: 'FLOW',
            text: String(row.text || ''),
            ['flow_id']: String(row.flowId || ''),
            ['flow_action']: 'NAVIGATE',
          }
          return button
        }
        return null
      })
      .filter(Boolean)
    if (buttons.length > 0) {
      out.push({
        type: 'BUTTONS',
        buttons,
      })
    }
    return out
  }, [
    form.headerType,
    form.headerText,
    form.headerTextVariableExample,
    form.bodyRichText,
    form.footerText,
    form.buttonRows,
    form.bodyVariableExamplesByToken,
    bodyPlainForVars,
    bodyVariableTokensInOrder,
    hasHeaderTextVariable,
  ])

  const renderLabel = (label, options = {}) => {
    const { required = false, tip = '' } = options
    return (
      <div className={css(styles.labelContainer)}>
        <p className={css(styles.label)}>{label}</p>
        {required && <img className={css(styles.requiredIndicator)} height='7' width='7' src={require('../../assets/images/emergency.svg')} />}
        {tip && (
          <Tooltip title={tip} classes={{ popper: css(styles.tooltip) }}>
            <i className='material-icons-outlined' style={{ fontSize: 14, color: '#7d7d7d', cursor: 'pointer', marginLeft: 5, marginTop: 0, alignSelf: 'center' }}>info</i>
          </Tooltip>
        )}
      </div>
    )
  }

  const renderTextField = (id, label, value, onChange, options = {}) => {
    const {
      multiline = false,
      rows = 5,
      required = false,
      tip = '',
      errorText = '',
    } = options
    return (
      <div key={id}>
        <FormControl className={css(styles.formControlMain)} fullWidth>
          {renderLabel(label, { required, tip })}
          <TextField
            variant='standard'
            className={css(styles.formControl)}
            value={value}
            disabled={inProcess}
            multiline={multiline}
            rows={multiline ? rows : undefined}
            onChange={(e) => {
              clearFieldError(id)
              onChange(e)
            }}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: css([styles.textInputStyles, Boolean(errorText) && styles.textInputStylesError, inProcess && styles.textInputStylesDisabled]),
                input: css(styles.formInputStyle, styles.textInput),
              },
            }}
          />
          {errorText ? <FormHelperText className={css(styles.fieldError)}>{errorText}</FormHelperText> : null}
        </FormControl>
      </div>
    )
  }

  const renderSelectField = (id, label, value, optionList, onChange, extra = {}) => {
    const selected = optionList.find(o => String(o.key) === String(value)) || null
    const controlSurfaceClass = extra.formControlSurfaceClass || styles.formControl
    const isDisabled = inProcess || !!extra.disabled
    return (
      <div key={id}>
        <FormControl className={css(styles.formControlMain)} fullWidth>
          {renderLabel(label, { required: !!extra.required, tip: extra.tip || '' })}
          <Autocomplete
            value={selected}
            options={optionList}
            disabled={isDisabled}
            onChange={(e, val) => {
              clearFieldError(id)
              onChange((val && val.key) || '')
            }}
            getOptionLabel={option => option.label || option.key || ''}
            getOptionSelected={(option, v) => option.key === (v && v.key)}
            disableClearable
            style={extra.autocompleteFullWidth ? { width: '100%' } : undefined}
            classes={{
              ...(extra.autocompleteFullWidth ? { root: css(styles.autocompleteRootFullWidth) } : {}),
              popupIndicator: css(styles.popupIndicator),
              popper: css(styles.dialog),
              option: css(styles.option),
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant='standard'
                className={css(controlSurfaceClass)}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  classes: {
                    root: css([styles.textInputStyles, styles.autoCompleteInputRoot, extra.errorText && styles.textInputStylesError, isDisabled && styles.textInputStylesDisabled]),
                    input: css(styles.formInputStyle, styles.textInput),
                  },
                }}
              />
            )}
          />
          {extra.errorText ? <FormHelperText className={css(styles.fieldError)}>{extra.errorText}</FormHelperText> : null}
        </FormControl>
      </div>
    )
  }

  return (
    <div className={css(styles.container, AppHelper.isDialogScrollbarEnabled() && styles.containerFitDialog)}>
      <div className={css(styles.contentLayout)}>
        <div className={css(styles.formPane)}>
          <form className={css(styles.form)} onSubmit={onSubmit}>
            <div className={css(styles.grid2)}>
              {renderSelectField(
                'wabaNumber',
                'WABA Number',
                form.wabaNumber,
                wabaOptions,
                val => patchField('wabaNumber', val),
                { required: true, tip: FIELD_TIPS.wabaNumber, disabled: initialForm ? false : true, errorText: fieldErrors.wabaNumber }
              )}
              {renderTextField('title', 'Name', form.title, e => patchField('title', e.target.value), { required: true, tip: FIELD_TIPS.title, errorText: fieldErrors.title })}
              {renderSelectField('iType', 'Category', form.iType, [
                { key: 'UTILITY', label: 'UTILITY' },
                { key: 'MARKETING', label: 'MARKETING' },
              ], val => patchField('iType', val), { required: true, errorText: fieldErrors.iType })}
              {renderSelectField('headerType', 'Header', form.headerType, [
                { key: 'NONE', label: 'NONE' },
                { key: 'TEXT', label: 'TEXT' },
                { key: 'IMAGE', label: 'IMAGE' },
                { key: 'VIDEO', label: 'VIDEO' },
                { key: 'DOCUMENT', label: 'DOCUMENT' },
              ], val => patchField('headerType', val), { required: true, errorText: fieldErrors.headerType })}
            </div>

            {form.headerType === 'TEXT' && (
              <div className={css(styles.grid2)}>
                {renderTextField('headerText', 'Header text', form.headerText, e => patchField('headerText', e.target.value), { required: true, tip: FIELD_TIPS.headerText, errorText: fieldErrors.headerText })}
                {hasHeaderTextVariable
                  ? renderTextField(
                    'headerTextVariableExample',
                    `Header text variable ${headerTextVariableToken}`,
                    form.headerTextVariableExample,
                    e => patchField('headerTextVariableExample', e.target.value),
                    { required: true, errorText: fieldErrors.headerTextVariableExample }
                  )
                  : <div />}
              </div>
            )}

            <div>
              <FormControl className={css(styles.formControlMain)} fullWidth>
                {renderLabel('Body text', { required: true, tip: FIELD_TIPS.bodyText })}
                <RichtextEditor
                  current={{
                    key: 'bodyRichText',
                    rows: 6,
                    toolbarFlags: 'CLEAR_FORMATTING, FORMAT_BOLD, FORMAT_ITALIC, BULLETS',
                  }}
                  fullEditor={false}
                  colorPicker={false}
                  value={form.bodyRichText}
                  variablePicker={{ title: 'Add Variable', automatic: true }}
                  disabled={inProcess}
                  stripPastedStyles={false}
                  stripPastedImages
                  clearFormatting
                  colorPickerData={{ isLoading: false, data: [] }}
                  handleColorChange={() => {}}
                  handleRichtextInputChange={handleRichtextInputChange}
                />
                {fieldErrors.bodyRichText ? <FormHelperText className={css(styles.fieldError)}>{fieldErrors.bodyRichText}</FormHelperText> : null}
              </FormControl>
            </div>
            {bodyVariableTokensInOrder.length > 0 && (
              <div className={css(styles.grid2)}>
                {bodyVariableTokensInOrder.map(token => renderTextField(
                  `body-var-${token}`,
                  `Body text variable example ${token}`,
                  (form.bodyVariableExamplesByToken && form.bodyVariableExamplesByToken[token]) || '',
                  e => patchBodyVariableExample(token, e.target.value),
                  { required: true, errorText: fieldErrors[`body-var-${token}`] }
                ))}
              </div>
            )}
            {renderTextField('footerText', 'Footer text', form.footerText, e => patchField('footerText', e.target.value), { tip: FIELD_TIPS.footerText })}

            <div className={css(styles.buttonsSection)}>
              <div className={css(styles.buttonsSectionHeader)}>
                <p className={css(styles.label, styles.buttonsSectionTitle)}>Buttons</p>
                <button
                  type='button'
                  className={css(styles.addButtonWithLabel)}
                  onClick={addButtonRow}
                  disabled={inProcess || form.buttonRows.length >= MAX_BUTTON_ROWS}
                  title={form.buttonRows.length >= MAX_BUTTON_ROWS ? `At most ${MAX_BUTTON_ROWS} buttons in this form` : 'Add button'}
                  aria-label='Add button'
                >
                  <i className={`material-icons-outlined ${css(styles.addButtonIconGlyph)}`}>add</i>
                  <span className={css(styles.addButtonLabelText)}>Add Button</span>
                </button>
              </div>
              {form.buttonRows.map(row => {
                const showUrlVar = row.type === 'URL' && urlHasVariablePlaceholder(row.url)
                const urlVariableMatch = String(row.url || '').match(/\{\{\d+\}\}/)
                const urlVariableToken = urlVariableMatch ? urlVariableMatch[0] : ''
                return (
                  <div key={row.id} className={css(styles.buttonRow)}>
                    <div className={css(styles.buttonRowGrid)}>
                      <div className={css(styles.buttonRowTypeWrap)}>
                        {renderSelectField(
                          `${row.id}-type`,
                          'Type',
                          row.type,
                          buttonTypeOptions,
                          val => updateButtonRow(row.id, { type: val }),
                          {
                            formControlSurfaceClass: styles.formControlButtonRow,
                            autocompleteFullWidth: true,
                            required: true,
                            errorText: fieldErrors[`${row.id}-type`],
                          }
                        )}
                      </div>
                      {row.type === 'QUICK_REPLY' && renderTextField(`${row.id}-text`, 'Text', row.text, e => updateButtonRow(row.id, { text: e.target.value }), { required: true, tip: FIELD_TIPS.buttonText, errorText: fieldErrors[`${row.id}-text`] })}
                      {row.type === 'PHONE' && (
                        <>
                          {renderTextField(`${row.id}-text`, 'Text', row.text, e => updateButtonRow(row.id, { text: e.target.value }), { required: true, tip: FIELD_TIPS.buttonText, errorText: fieldErrors[`${row.id}-text`] })}
                          {renderTextField(`${row.id}-phone`, 'Phone', row.phone, e => updateButtonRow(row.id, { phone: e.target.value }), { required: true, tip: FIELD_TIPS.buttonPhone, errorText: fieldErrors[`${row.id}-phone`] })}
                        </>
                      )}
                      {row.type === 'URL' && (
                        <>
                          {renderTextField(`${row.id}-text`, 'Text', row.text, e => updateButtonRow(row.id, { text: e.target.value }), { required: true, tip: FIELD_TIPS.buttonText, errorText: fieldErrors[`${row.id}-text`] })}
                          {renderTextField(`${row.id}-url`, 'URL', row.url, e => updateButtonRow(row.id, { url: e.target.value }), { required: true, tip: FIELD_TIPS.buttonUrl, errorText: fieldErrors[`${row.id}-url`] })}
                          {showUrlVar && renderTextField(`${row.id}-urlVar`, `URL variable ${urlVariableToken}`, row.urlVariable, e => updateButtonRow(row.id, { urlVariable: e.target.value }), { required: true, errorText: fieldErrors[`${row.id}-urlVar`] })}
                        </>
                      )}
                      {row.type === 'FLOW' && (
                        <>
                          {renderTextField(`${row.id}-text`, 'Text', row.text, e => updateButtonRow(row.id, { text: e.target.value }), { required: true, tip: FIELD_TIPS.buttonText, errorText: fieldErrors[`${row.id}-text`] })}
                          <div className={css(styles.flowSelectWrap)}>
                            {renderSelectField(
                              `${row.id}-flowId`,
                              'Flow',
                              row.flowId,
                              flowOptions.length > 0 ? flowOptions : [{ key: '', label: 'No flows available' }],
                              val => updateButtonRow(row.id, { flowId: val }),
                              { required: true, tip: FIELD_TIPS.buttonFlow, errorText: fieldErrors[`${row.id}-flowId`], autocompleteFullWidth: true, formControlSurfaceClass: styles.formControlButtonRow }
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      type='button'
                      className={css(styles.removeRowButton)}
                      onClick={() => removeButtonRow(row.id)}
                      disabled={inProcess}
                      aria-label='Remove button'
                      title='Remove'
                    >
                      <i className='material-icons-outlined'>close</i>
                    </button>
                  </div>
                )
              })}
            </div>

            <div className={css(styles.actions)}>
              <span className={isError ? css(styles.errorMsg) : css(styles.successMsg)}>
                <span dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(message) }}></span>
              </span>
              <AppButton
                buttonStyle={styles.button}
                buttonTextStyle={styles.buttonText}
                disabled={inProcess}
                inProcess={inProcess}
                onClick={submitTemplate}
                buttonText='Create'
              />
            </div>
          </form>
        </div>
        <div className={css(styles.previewPane)}>
          <div className={css(styles.previewSticky)}>
            <div className={css(styles.previewTitle)}>Preview</div>
            <WhatsappTemplatePreview components={previewComponents} />
          </div>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    height: '78vh',
    minHeight: 640,
    maxHeight: '78vh',
    overflow: 'hidden',
    '@media (max-width: 1024px)': {
      minWidth: 0,
      maxWidth: '100%',
      height: 'auto',
      minHeight: 0,
      maxHeight: 'none',
      overflow: 'visible',
    },
    '& .rdw-dropdown-optionwrapper': {
      backgroundColor: '#ffffff',
      opacity: 1,
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)',
      zIndex: 130001,
    },
    '& .rdw-dropdownoption-default': {
      backgroundColor: '#ffffff',
      opacity: 1,
    },
    '& .rdw-dropdown-selectedtext': {
      backgroundColor: '#ffffff',
      opacity: 1,
    },
  },
  containerFitDialog: {
    minHeight: 'min(640px, calc(100vh - 144px))',
    maxHeight: 'min(78vh, calc(100vh - 144px))',
  },
  contentLayout: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
    height: '100%',
    minHeight: 0,
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      height: 'auto',
    },
  },
  formPane: {
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 10,
    paddingBottom: 24,
    scrollbarGutter: 'stable',
    '@media (max-width: 1024px)': {
      overflow: 'visible',
      paddingRight: 0,
      paddingBottom: 0,
    },
  },
  previewPane: {
    flex: '0 0 320px',
    width: 320,
    minWidth: 300,
    maxWidth: 320,
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 4,
    scrollbarGutter: 'stable',
    '@media (max-width: 1024px)': {
      flex: '1 1 auto',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      height: 'auto',
      overflow: 'visible',
      paddingRight: 0,
    },
  },
  previewSticky: {
    position: 'static',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 4,
    '@media (max-width: 1024px)': {
      position: 'static',
      paddingTop: 8,
      marginTop: 6,
    },
  },
  previewTitle: {
    width: '100%',
    fontSize: 15,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 8,
    paddingLeft: 2,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#fff',
    paddingTop: 2,
    paddingBottom: 6,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    width: '100%',
    maxWidth: 900,
    '@media (max-width: 1024px)': {
      maxWidth: '100%',
    },
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    '@media (max-width: 767px)': {
      gridTemplateColumns: '1fr',
    },
  },
  label: {
    fontSize: 14,
    color: 'grey',
    margin: 0,
    display: 'flex',
  },
  labelContainer: {
    display: 'flex',
    marginBottom: 8,
  },
  requiredIndicator: {
    opacity: 0.5,
    height: 7,
    width: 7,
    alignSelf: 'center',
    marginLeft: 4,
  },
  tooltip: {
    zIndex: 9999999999,
  },
  formControlMain: {
    marginTop: 0,
    marginBottom: 0,
  },
  formControl: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  formControlButtonRow: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'visible',
    width: '100%',
  },
  autocompleteRootFullWidth: {
    width: '100%',
    maxWidth: '100%',
  },
  textInput: {
    padding: '4px 0px',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  textInputStylesDisabled: {
    backgroundColor: '#f5f5f5',
  },
  textInputStylesError: {
    borderColor: '#f44336',
  },
  fieldError: {
    color: '#f44336',
    marginTop: 6,
    marginLeft: 2,
    fontSize: 12,
    lineHeight: '16px',
  },
  autoCompleteInputRoot: {
    paddingRight: 56,
  },
  popupIndicator: {
    marginRight: 5,
  },
  dialog: {
    zIndex: 130000,
    fontSize: 16,
    color: '#222222',
  },
  option: {
    fontSize: 16,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginTop: 20,
    marginBottom: 10,
  },
  errorMsg: {
    paddingRight: 20,
    color: '#f44336',
  },
  successMsg: {
    paddingRight: 20,
    color: 'green',
  },
  button: {
    backgroundColor: AppTheme.buttonBg,
  },
  buttonText: {
    color: AppTheme.buttonTextColor,
  },
  buttonsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  buttonsSectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  buttonsSectionTitle: {
    marginBottom: 0,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  buttonRowGrid: {
    flex: '1 1 auto',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
    overflowX: 'visible',
    paddingBottom: 4,
    paddingRight: 2,
    '& > div:not(:first-child)': {
      flex: '1 1 160px',
      width: 'auto',
      minWidth: 140,
      maxWidth: '100%',
    },
  },
  buttonRowTypeWrap: {
    flex: '0 0 188px',
    width: 188,
    minWidth: 188,
    maxWidth: 188,
    flexShrink: 0,
    alignSelf: 'stretch',
    '& > div': {
      width: '100%',
      minWidth: 0,
    },
  },
  flowSelectWrap: {
    flex: '1.7 1 300px',
    minWidth: 280,
    maxWidth: '100%',
  },
  addButtonWithLabel: {
    border: `1px solid ${AppTheme.primaryColor || '#2563EB'}`,
    borderRadius: AppTheme.borderRadius,
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    padding: '10px 18px',
    minHeight: 40,
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: AppTheme.primaryColor || '#2563EB',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    outline: 'none',
    transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    ':hover': {
      backgroundColor: 'rgba(37, 99, 235, 0.07)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
    },
    ':active': {
      backgroundColor: 'rgba(37, 99, 235, 0.11)',
    },
    ':disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  },
  addButtonIconGlyph: {
    fontSize: 20,
    lineHeight: 1,
    display: 'block',
  },
  addButtonLabelText: {
    lineHeight: 1,
    letterSpacing: 0.2,
  },
  removeRowButton: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 8,
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 30,
    marginLeft: 4,
    color: '#6B7280',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#111827',
    },
    ':disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
})

export default WhatsappTemplateAddForm
